import {
  Box,
  ColumnLayout,
  Container,
  Header,
  Popover,
  SpaceBetween,
  StatusIndicator,
} from "@amzn/awsui-components-console";
import React from "react";

import { AnalyticsKpis } from "@/hooks/admin/useAdminUserAnalytics";

import {
  computeTrend,
  formatCount,
  formatDurationSeconds,
  formatRate,
  formatTokens,
  formatUsd,
  NOT_AVAILABLE,
  Trend,
} from "./format";

interface AnalyticsKpiCardsProps {
  kpis: AnalyticsKpis;
}

/** Trend line under a figure. Renders nothing when no comparison is possible. */
const TrendNote: React.FC<{ trend: Trend | null }> = ({ trend }) => {
  if (!trend) {
    return null;
  }
  if (trend.direction === "up" || trend.direction === "down") {
    return (
      <StatusIndicator
        type={trend.direction === "up" ? "success" : "info"}
        colorOverride={trend.direction === "up" ? "green" : "blue"}
      >
        {trend.label}
      </StatusIndicator>
    );
  }
  return (
    <Box variant="small" color="text-body-secondary">
      {trend.label}
    </Box>
  );
};

const Kpi: React.FC<{
  label: string;
  value: string;
  footnote?: React.ReactNode;
}> = ({ label, value, footnote }) => (
  <Box>
    <Box variant="awsui-key-label">{label}</Box>
    <Box variant="h2">{value}</Box>
    {footnote}
  </Box>
);

/**
 * Headline usage figures for the selected window.
 *
 * Where the API reports null the card shows a placeholder and says why, rather
 * than a zero: no known rate is not free, no completed session is not a
 * zero-second session, and no preceding window is not a flat trend.
 */
const AnalyticsKpiCards: React.FC<AnalyticsKpiCardsProps> = ({ kpis }) => {
  const usersTrend = computeTrend(kpis.activeUsers, kpis.priorActiveUsers);
  const questionsTrend = computeTrend(
    kpis.questionsAsked,
    kpis.priorQuestionsAsked
  );

  return (
    <Container header={<Header variant="h2">Usage summary</Header>}>
      <ColumnLayout columns={4} variant="text-grid">
        <Kpi
          label="Active users"
          value={formatCount(kpis.activeUsers)}
          footnote={
            <SpaceBetween size="xxs">
              <Box variant="small" color="text-body-secondary">
                Asked at least one question
              </Box>
              <TrendNote trend={usersTrend} />
            </SpaceBetween>
          }
        />
        <Kpi
          label="Questions asked"
          value={formatCount(kpis.questionsAsked)}
          footnote={
            <SpaceBetween size="xxs">
              <Box variant="small" color="text-body-secondary">
                {formatRate(kpis.questionsPerUser)} per active user
              </Box>
              <TrendNote trend={questionsTrend} />
            </SpaceBetween>
          }
        />
        <Kpi
          label="Tokens"
          value={formatTokens(kpis.totalTokens)}
          footnote={
            <Box variant="small" color="text-body-secondary">
              {formatTokens(kpis.inputTokens)} in &middot;{" "}
              {formatTokens(kpis.outputTokens)} out
            </Box>
          }
        />
        <Kpi
          label="Estimated cost"
          value={formatUsd(kpis.estimatedCostUsd)}
          footnote={
            kpis.costIsPartial ? (
              <Popover
                header="Cost is incomplete"
                position="top"
                triggerType="text"
                content={
                  <SpaceBetween size="xs">
                    <Box variant="p">
                      Usage from these models has no configured rate, so their
                      cost is excluded from this total:
                    </Box>
                    <Box variant="code">
                      {kpis.unpricedModelIds.join(", ") || NOT_AVAILABLE}
                    </Box>
                  </SpaceBetween>
                }
              >
                <StatusIndicator type="warning" data-testid="cost-partial-badge">
                  Excludes unpriced models
                </StatusIndicator>
              </Popover>
            ) : (
              <Box variant="small" color="text-body-secondary">
                All models in this period are priced
              </Box>
            )
          }
        />
        <Kpi
          label="Sessions"
          value={formatCount(kpis.observedSessions)}
          footnote={
            <Box variant="small" color="text-body-secondary">
              Completed connections measured
            </Box>
          }
        />
        <Kpi
          label="Average session"
          value={formatDurationSeconds(kpis.avgSessionSeconds)}
          footnote={
            <Box variant="small" color="text-body-secondary">
              {kpis.avgSessionSeconds === null
                ? "No completed session to measure"
                : "Across completed sessions"}
            </Box>
          }
        />
      </ColumnLayout>
    </Container>
  );
};

export default AnalyticsKpiCards;
